import {useEffect,useState} from 'react';
import {z} from 'zod';
import {httpBoundary} from '@werkingflow/trust-boundary';
import {ZielinventarDokumentSchema,aktuellesZielinventar,type ZielinventarDokument,type ZielEintrag} from '../../shared/guide-zielinventar';
const AntwortSchema=z.object({zielinventar:ZielinventarDokumentSchema.nullable()});
const bezeichnung:Record<ZielEintrag['zustand'],string>={offen:'Offen',in_arbeit:'In Bearbeitung',blockiert:'Abhängigkeit offen',erledigt:'Erledigt',zurueckgestellt:'Zurückgestellt',entfallen:'Durch Nutzerentscheidung entfallen'};

// This only selects text for the compact view; it never rewrites or interprets it.
// Longer/technical source text remains available verbatim in the closed details.
function kurzLesbar(text:string,max=220):boolean {
 return !!text.trim() && text.length<=max
  && !/https?:\/\/|(?:^|\s)\/\S+|\b[\w.-]+\.(?:md|json|jsonl|tsx?|ya?ml|html|py)\b|\b[0-9a-f]{8}-[0-9a-f-]{27,}\b|\b(?:session|owner|branch|commit|merge|deploy|codex)\b|\b(?:feat|fix|refs)\/|\bWeg\s+[A-Z]\b/i.test(text);
}

/** Read-only presentation; hierarchy, sources and every historical version stay intact. */
export function GuideZielinventarInhalt({doc,offeneFragenAnzahl}:{doc:ZielinventarDokument;offeneFragenAnzahl?:number}) {
 const f=aktuellesZielinventar(doc)!;
 const namen=(ids:string[])=>ids.map(id=>f.eintraege.find(e=>e.id===id)?.titel??id).join('; ');
 const fachlich=f.eintraege.filter(e=>e.ebene!=='technik');
 const eltern=new Set(fachlich.flatMap(e=>e.elternId?[e.elternId]:[]));
 const blaetter=fachlich.filter(e=>!eltern.has(e.id)).sort((a,b)=>Number(b.ebene==='teilziel')-Number(a.ebene==='teilziel'));
 // Only remove duplicate labels with the same state from the overview, never from data.
 const gesehen=new Set<string>();
 const ziele=blaetter.filter(e=>{
  const key=`${e.titel.trim().toLocaleLowerCase('de')}\0${e.zustand}`;
  if(gesehen.has(key))return false;gesehen.add(key);return true;
 });
 const importiert=f.von==='system:bestandsuebernahme';
 const lesbareZiele=ziele.filter(e=>kurzLesbar(e.titel,140));
 const offen=ziele.filter(e=>['offen','in_arbeit','blockiert'].includes(e.zustand));
 const erledigt=ziele.filter(e=>e.zustand==='erledigt');
 const vorschau=[...lesbareZiele.filter(e=>['offen','in_arbeit','blockiert'].includes(e.zustand)),...lesbareZiele.filter(e=>!['offen','in_arbeit','blockiert'].includes(e.zustand))].slice(0,3);
 // Length or technical terms do not prove that no working plan exists. Show the
 // actual saved block; the adviser remains responsible for its clarity/currentness.
 const arbeitsstrecke=f.arbeitsstrecke.trim();
 const zeigeDetails=(e:ZielEintrag)=><li key={e.id} data-ziel-id={e.id} style={{marginBottom:10}}>
  <strong>{e.titel}</strong> · {bezeichnung[e.zustand]}
  {e.elternId&&<div>Teil von: {f.eintraege.find(p=>p.id===e.elternId)?.titel??e.elternId}</div>}
  <p>{e.fertigWenn}{e.kriteriumOffen?' (noch zu präzisieren)':''}</p>
  <p>Quelle: {f.quellen.find(q=>q.art===e.quelle.art&&q.id===e.quelle.id)?.text??`${e.quelle.art}: ${e.quelle.id}`}</p>
  {e.ableitung&&<p>Ableitung: {e.ableitung}</p>}
  <p>Nächster Schritt: {e.naechsterSchritt||'Noch nicht angegeben.'}</p>
  {e.belege.length?<ul>{e.belege.map((b,i)=><li key={i}>{b.befund} · {b.referenz} · {b.am}</li>)}</ul>:<p>Noch kein Ergebnisbeleg.</p>}
  {e.abhaengigkeiten.map((a,i)=><p key={i}>{a.grund} · Zuständig: {a.zustaendig} · {a.naechsterSchritt}</p>)}
 </li>;
 return <section aria-label="Zielübersicht" data-ai-id="guide-zielinventar" style={{borderTop:'1px solid var(--border-color,#444)',paddingTop:8,marginTop:8,minWidth:0,overflowWrap:'anywhere',fontSize:'0.9em'}}>
  <div style={{display:'flex',flexWrap:'wrap',alignItems:'baseline',gap:'4px 10px'}}>
   <strong>Teilziele</strong>
   {!importiert&&ziele.length>0&&<span>{offen.length} offen · {erledigt.length} erledigt</span>}
   {offeneFragenAnzahl!==undefined&&offeneFragenAnzahl>0&&<span>{offeneFragenAnzahl} persönliche {offeneFragenAnzahl===1?'Auswahl':'Auswahlen'} im Fragenblock</span>}
  </div>
  {importiert||lesbareZiele.length===0
   ? <p style={{margin:'6px 0'}}>{lesbareZiele[0]&&<><strong>{lesbareZiele[0].titel}</strong><br/></>}Teilziele werden noch aufbereitet. Die übernommenen Angaben bleiben in den Details erhalten.</p>
   : <>
    <ul style={{margin:'6px 0',paddingLeft:18}}>{vorschau.map(e=>{
     const belege=e.belege.filter(b=>b.art==='ergebnis'||b.art==='pruefung');
     const beleg=belege.at(-1);
     return <li key={e.id} style={{marginBottom:4}}><span>{e.titel}</span> <small>· {bezeichnung[e.zustand]}</small>
      {beleg&&kurzLesbar(beleg.befund,160)?<small style={{display:'block'}}>Beleg: {beleg.befund}</small>
       : e.zustand==='erledigt'&&!beleg?<small style={{display:'block'}}>Ergebnisbeleg fehlt im Zielinventar.</small>:null}
     </li>;
    })}</ul>
    {ziele.length>vorschau.length&&<small>Weitere {ziele.length-vorschau.length} Teilziele in den Details.</small>}
    {arbeitsstrecke?<p style={{margin:'6px 0'}}><strong>Nächste Arbeit:</strong> {arbeitsstrecke}</p>
     : <p style={{margin:'6px 0'}}>Für die nächste Arbeitsstrecke fehlt eine konkrete Angabe.</p>}
   </>}
  <details><summary style={{cursor:'pointer',padding:'4px 0'}}>Details, Belege und Verlauf</summary>
   <p>Arbeitsstrecke (Original): {f.arbeitsstrecke}</p>
   <details><summary>Alle Ziele und technische Ausführung ({f.eintraege.length})</summary><ul style={{paddingLeft:18}}>{f.eintraege.map(zeigeDetails)}</ul></details>
   <details><summary>Änderungen seit dem letzten Stand</summary>
    <p>{f.delta.grund}</p>
    {f.delta.neu.length>0&&<p>Neu: {namen(f.delta.neu)}</p>}
    {f.delta.erledigt.length>0&&<p>Erledigt: {namen(f.delta.erledigt)}</p>}
    {f.delta.geaendert.length>0&&<p>Geändert: {namen(f.delta.geaendert)}</p>}
    <p>Weiter offen: {f.delta.weiterOffen.length?namen(f.delta.weiterOffen):'Keine offenen Teilziele in dieser Fassung. Den Themenabschluss bestimmst du.'}</p>
   </details>
   <details><summary>Vollständige Quellen und bisherige Fassungen ({doc.fassungen.length})</summary><pre style={{whiteSpace:'pre-wrap',overflowWrap:'anywhere',fontSize:'0.85em'}}>{JSON.stringify(doc,null,2)}</pre></details>
  </details>
 </section>;
}

export function GuideZielinventar({buehneId,aktuelleVersion,offeneFragenAnzahl}:{buehneId:string;aktuelleVersion?:number;offeneFragenAnzahl?:number}){
 const [doc,setDoc]=useState<ZielinventarDokument|null>(null),[fehler,setFehler]=useState('');
 useEffect(()=>{let active=true;setDoc(null);setFehler('');
  httpBoundary({name:'cui.zielinventar.lesen',url:`/api/guide/buehnen/${encodeURIComponent(buehneId)}/zielinventar`,schema:AntwortSchema,timeoutMs:15000})
   .then(r=>{if(active)setDoc(r.zielinventar);}).catch(e=>{if(active)setFehler(e instanceof Error?e.message:'Zielinventar konnte nicht geladen werden.');});
  return()=>{active=false;};
 },[buehneId,aktuelleVersion]);
 if(fehler)return <p role="alert">{fehler}</p>;
 if(!doc)return <p data-ai-id="guide-zielinventar-fehlt">Teilziele werden noch aufbereitet.</p>;
 return <GuideZielinventarInhalt doc={doc} offeneFragenAnzahl={offeneFragenAnzahl}/>;
}
